import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import DoubleTranslatedCustomRichText from '@/components/CustomRichText/DoubleTranslatedCustomRichText';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import InfoMsg from '@/components/UI/InfoMsg/InfoMsg';
import Spinner from '@/components/UI/Spinner/Spinner';
import * as Constants from '@/helpers/constants';
import { getEditorId, removeParagraphTags, tabLowerCase } from '@/helpers/utils';
import useToastContext from '@/hooks/useToastContext';
import { setHelpSummary } from '@/redux/Help/help.action';
import { getHelpSummary } from '@/redux/Help/help.reducer';
import administrationServices from '@/services/administrationServices';
import { ReduxHelp } from '@/types';
import styles from '../HelpText.module.scss';

interface SummaryHelpTextProps {
  helpSummary: ReduxHelp;
  setSummary: (helpDesc: ReduxHelp) => void;
}

const SummaryHelpText: React.FC<SummaryHelpTextProps> = (props) => {
  const { t } = useTranslation();
  const tab = tabLowerCase(window.location.pathname.split('/')[4]);
  const [originalValues, setOriginalValues] = useState({});
  const { helpSummary, setSummary } = props;
  const isLoading = helpSummary && helpSummary && !helpSummary.SUMMARY_TAB;
  const { addToast } = useToastContext();
  const SUMMARY_TAB = 'SUMMARY_TAB';
  const SUMMARY_PROBLEM_OVERVIEW = 'SUMMARY_PROBLEM_OVERVIEW';
  const SUMMARY_AFFECTED_VEHICLES = 'SUMMARY_AFFECTED_VEHICLES';

  const ID_SUMMARY_GENERAL = 'summary-general';
  const ID_SUMMARY_PROBLEM = 'summary-problem';
  const ID_SUMMARY_AFFECTED_VEHICLE = 'summary-affectedVehicle';

  useEffect(() => {
    administrationServices.getHelp(tab)
      .then((data) => {
        const values = helpSummary.modified ? helpSummary : data;
        setSummary(values);
        setOriginalValues(values);
      })
      .catch((error) => addToast(error.message, 'ERROR'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWysiwygChange = (lang, label, data) => {
    const propValues = { ...helpSummary[label] };
    const isModified = removeParagraphTags(data) !== removeParagraphTags(originalValues[label][lang]);
    const newState = {
      ...helpSummary,
      [label]: {
        ...propValues,
        [lang]: data,
      },
      modified: isModified,
    };

    if (isModified) {
      setSummary(newState as unknown as ReduxHelp);
      setOriginalValues(newState);
    }
  };

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <InfoMsg msg={t('administration.helpText.info')} />
          <AccordionPanel title={t('administration.helpText.generalText')}>
            <DoubleTranslatedCustomRichText
              id={ID_SUMMARY_GENERAL}
              title={t('helptext')}
              initialValue={helpSummary?.SUMMARY_TAB}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, SUMMARY_TAB, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('summary.title')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_SUMMARY_PROBLEM}
              title={t('helptext')}
              initialValue={helpSummary?.SUMMARY_PROBLEM_OVERVIEW}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, SUMMARY_PROBLEM_OVERVIEW, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('plantFeedback.affectedVehiclesTable')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_SUMMARY_AFFECTED_VEHICLE}
              title={t('helptext')}
              initialValue={helpSummary?.SUMMARY_AFFECTED_VEHICLES}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, SUMMARY_AFFECTED_VEHICLES, newValue)}
            />
          </AccordionPanel>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  helpSummary: getHelpSummary(state),
});

const actionCreators = {
  setSummary: setHelpSummary,
};

export default connect(mapStateToProps, actionCreators)(SummaryHelpText);
